/**
 * Hardware-attested user verification.
 *
 * Everything else in this protocol is checked by mathematics. Liveness was
 * the exception: a claim asserted "a living person was in front of a
 * camera" and no validator could test it, because verification ran on the
 * claimant's own hardware. A modified client simply lied.
 *
 * A platform authenticator closes that gap for the part that matters most.
 * When a phone's secure element signs with `userVerification` required, it
 * signs only after its own biometric check has passed - Face ID's infrared
 * depth map, Touch ID's capacitive ridge sensor, or a device passcode -
 * and those run in hardware the browser cannot reach and a script cannot
 * imitate. The resulting signature is verifiable by anyone.
 *
 * That buys three things this system could not otherwise have:
 *
 *   1. A second key factor that a photograph does not yield. The wallet
 *      key mixes a secret derived inside the secure element (the WebAuthn
 *      PRF extension) with the face-derived secret. Holding someone's
 *      photograph now gives an attacker one half of a key and no way to
 *      compute the other.
 *   2. Liveness that a validator can check. Not our liveness - the
 *      platform's, which is materially better than anything achievable in
 *      JavaScript, and which is enforced where the attacker is not.
 *   3. Per-transaction consent. Spending requires a fresh assertion over
 *      that exact vertex, so a stolen key alone moves nothing.
 *
 * What it does not buy: a corpse or an unconscious person can still defeat
 * a fingerprint sensor, and a device passcode is a fallback the owner may
 * be forced to give up. Section 6 of SECURITY.md is explicit about that.
 */

import { p256 } from '@noble/curves/nist';
import { sha256 } from '@noble/hashes/sha2';
import { concat, equalBytes, fromHex, toHex, type Hex } from './bytes.ts';

/** A registered authenticator, published with the claim that created it. */
export interface DeviceCredential {
  /** Credential id, base64url as the platform reports it. */
  id: string;
  /**
   * The credential's P-256 public key as an uncompressed point, hex.
   *
   * Deliberately not the COSE_Key the authenticator emits. A CBOR reader
   * in a consensus path is somewhere a malformed key can take one node
   * down a different road from another; 65 bytes beginning 0x04 has one
   * reading and no options.
   */
  pub: Hex;
}

/** One signed assertion from that authenticator. */
export interface DeviceAssertion {
  /** Authenticator data, hex. Carries the flags and the counter. */
  auth: Hex;
  /** clientDataJSON, hex. Carries the challenge and the origin. */
  client: Hex;
  /** The signature, hex. DER for ES256 as WebAuthn produces it. */
  sig: Hex;
}

export const FLAG_USER_PRESENT = 0x01;
export const FLAG_USER_VERIFIED = 0x04;

export interface AssertionResult {
  ok: boolean;
  reason?: string;
  /** The challenge the authenticator actually signed over. */
  challenge?: Hex;
  userPresent: boolean;
  userVerified: boolean;
  origin?: string;
}

/* ------------------------- key extraction ---------------------------- */

/**
 * Pull the uncompressed P-256 point out of the SPKI DER that
 * `AuthenticatorAttestationResponse.getPublicKey()` returns.
 *
 * For P-256 the point is the final 65 bytes and begins 0x04. Anything else
 * is a key shape this protocol does not accept, and saying so is better
 * than guessing.
 */
export function spkiToP256(spki: Uint8Array): Uint8Array | null {
  if (spki.length < 65) return null;
  const point = spki.slice(spki.length - 65);
  if (point[0] !== 0x04) return null;
  return point;
}

/* --------------------------- verification --------------------------- */

/**
 * Verify one assertion, exactly as a relying party would.
 *
 * `expectedChallenge` is the vertex the wallet is asking the authenticator
 * to consent to, so an assertion captured from one transaction cannot be
 * replayed onto another.
 */
export function verifyAssertion(
  credential: DeviceCredential,
  assertion: DeviceAssertion,
  expectedChallenge: Uint8Array,
): AssertionResult {
  const fail = (reason: string): AssertionResult =>
    ({ ok: false, reason, userPresent: false, userVerified: false });

  let auth: Uint8Array;
  let client: Uint8Array;
  let sig: Uint8Array;
  let point: Uint8Array;
  try {
    auth = fromHex(assertion.auth);
    client = fromHex(assertion.client);
    sig = fromHex(assertion.sig);
    point = fromHex(credential.pub);
  } catch {
    return fail('malformed assertion encoding');
  }
  if (point.length !== 65 || point[0] !== 0x04) return fail('unsupported credential key');
  if (auth.length < 37) return fail('authenticator data too short');

  const flags = auth[32];
  const userPresent = (flags & FLAG_USER_PRESENT) !== 0;
  const userVerified = (flags & FLAG_USER_VERIFIED) !== 0;

  let clientData: { type?: string; challenge?: string; origin?: string };
  try {
    clientData = JSON.parse(new TextDecoder().decode(client));
  } catch {
    return fail('client data is not JSON');
  }
  if (clientData.type !== 'webauthn.get') return fail('assertion is not a webauthn.get');

  // The challenge is base64url of the bytes the wallet asked for.
  const got = b64urlDecode(clientData.challenge ?? '');
  if (!got || !equalBytes(got, expectedChallenge)) {
    return { ...fail('the authenticator signed a different challenge'), userPresent, userVerified };
  }

  // WebAuthn signs SHA-256(authData || SHA-256(clientDataJSON)).
  const message = concat(auth, sha256(client));
  let valid = false;
  try {
    valid = p256.verify(sig, sha256(message), point, { prehash: false, format: 'der' });
  } catch {
    valid = false;
  }
  if (!valid) return { ...fail('assertion signature does not verify'), userPresent, userVerified };

  return {
    ok: true,
    challenge: toHex(got),
    userPresent,
    userVerified,
    origin: clientData.origin,
  };
}

function b64urlDecode(s: string): Uint8Array | null {
  if (typeof s !== 'string' || !/^[A-Za-z0-9_-]*$/.test(s)) return null;
  const pad = s.length % 4 === 2 ? '==' : s.length % 4 === 3 ? '=' : '';
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
  try {
    // atob everywhere it exists - browsers, Workers, and Node 16+ - so the
    // consensus path has one implementation rather than a fork.
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

export function b64urlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  const b64 = btoa(bin);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
